import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const sections = [
    {
      title: 'Introduction',
      content: 'SMR Online ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Matka application.'
    },
    {
      title: 'Scope & Applicability',
      content: 'This Policy applies to all users of SMR Online services, including the mobile application and website. By using our services, you consent to the data practices described in this policy.'
    },
    {
      title: 'Information We Collect',
      content: 'We collect personal information including name, email, phone number, and payment details. We also collect device information, usage data, and location data to improve our services.'
    },
    {
      title: 'Methods of Collection',
      content: 'We collect information directly from you when you register, make transactions, or contact support. We also collect data automatically through cookies and similar technologies.'
    },
    {
      title: 'Purpose of Processing',
      content: 'We process your data to provide and improve our services, process transactions, send notifications, ensure security, and comply with legal obligations.'
    },
    {
      title: 'Data Sharing & Disclosure',
      content: 'We may share your information with payment processors, legal authorities when required, and service providers who assist in our operations. We do not sell your personal information.'
    },
    {
      title: 'Data Retention',
      content: 'We retain your personal data for as long as your account is active or as needed to provide services. We may retain certain information for legal compliance and dispute resolution.'
    },
    {
      title: 'User Rights',
      content: 'You have the right to access, correct, delete, and port your data. You can also opt-out of marketing communications and withdraw consent at any time.'
    },
    {
      title: "Children's Privacy",
      content: 'SMR Online is not intended for users under 18 years of age. We do not knowingly collect information from minors. If we discover such data, we will delete it immediately.'
    },
    {
      title: 'Data Security',
      content: 'We implement AES-256 encryption, TLS 1.3, and follow ISO 27001 standards to protect your data. However, no method of transmission is 100% secure.'
    },
    {
      title: 'International Transfers',
      content: 'Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers.'
    },
    {
      title: 'Cookie Policy',
      content: 'We use cookies and similar technologies to enhance your experience, analyze usage, and personalize content. You can manage cookie preferences in your browser settings.'
    },
    {
      title: 'Grievance Redressal',
      content: 'For any privacy concerns or complaints, contact our Grievance Officer at support@smronline.org or legal@smronline.org.'
    },
    {
      title: 'Policy Updates',
      content: 'We may update this Privacy Policy periodically. We will notify you of significant changes through the app or email. Continued use constitutes acceptance of updates.'
    },
    {
      title: 'Governing Law',
      content: 'This Privacy Policy is governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Mumbai, Maharashtra.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-green-50">
      {/* Header */}
      <motion.header
        className="bg-white/80 backdrop-blur-sm border-b border-gray-100 pt-20"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="w-full max-w-[85%] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <ArrowLeft className="w-5 h-5 text-primary group-hover:text-primary-dark transition-colors duration-300" />
              <span className="text-primary group-hover:text-primary-dark transition-colors duration-300">Back to Home</span>
            </Link>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gradient">
                Privacy Policy
              </h1>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="w-full max-w-[85%] mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Hero Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1 className="text-4xl lg:text-6xl font-bold mb-6 text-gray-900">
            Privacy{' '}
            <span className="text-gradient">Policy</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Your privacy is important to us. Learn how we protect your data.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Effective Date: 11th October 2025 | Company: SMR Online
          </p>
        </motion.div>

        {/* Policy Content */}
        <motion.div
          className="bg-white rounded-3xl p-8 lg:p-12 shadow-lg border border-gray-100 space-y-8"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              viewport={{ once: true }}
            >
              <div className="flex items-start gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center text-white font-bold text-sm">
                  {index + 1}
                </span>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {section.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
              {index < sections.length - 1 && (
                <hr className="mt-8 border-gray-100" />
              )}
            </motion.div>
          ))}
        </motion.div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
